/**
 * Request/response types and status constants for patient-graph ingest APIs.
 *
 * @module patientGraph/types
 */

/** Terminal outcomes the ingest endpoint reports inside a 200 response. */
export const INGEST_IN_PATIENT_GRAPH_STATUS = {
  INGESTED: "ingested",
  SKIPPED: "skipped",
  FAILED: "failed",
} as const;

export type IngestInPatientGraphStatus =
  (typeof INGEST_IN_PATIENT_GRAPH_STATUS)[keyof typeof INGEST_IN_PATIENT_GRAPH_STATUS];

/** Body for POST `/medical/patient-graph/ingest-pa-order`. */
export interface IngestInPatientGraphPayload {
  /** Medical PA order identifier. */
  order_id: string;
  /** Healthcare facility / organization identifier. */
  org_id: string;
  /** Patient MRN used for request logging; ingest still reads MRN from the order. */
  patient_mrn: string;
}

/**
 * Outcome of a patient-graph ingest attempt.
 *
 * Skipped and failed attempts still return HTTP 200, so callers must read
 * `status` rather than treat any non-throwing response as a success.
 */
export interface IngestInPatientGraphResponse {
  /** Medical PA order identifier echoed by the backend. */
  order_id: string;
  /** `ingested`, `skipped`, or `failed`. */
  status: IngestInPatientGraphStatus;
  /** Why ingest was skipped or failed, when applicable. */
  reason?: string | null;
}

/**
 * Whether a patient's medical graph has already been ingested for an org.
 */
export interface PmgIngestionExistsResponse {
  /** Patient medical record number echoed by the graph service. */
  mrn: string;
  /** Healthcare facility / organization identifier. */
  org_id: string;
  /** True when graph data already exists for this MRN and org. */
  exists: boolean;
  /** ISO timestamp of the most recent ingest, when present. */
  ingested_at: string | null;
}
